import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from '../utils/axios';
import { CONCURENS_API_ADMIN_BASE } from '../constants/concurensModuleUrls';
import { reportSessionUnauthorized } from '../utils/sessionGuard';
import { selectCheckingAuth, selectIsAuthenticated } from '../store/authSlice';

/**
 * Server-side ACL for Concurens (access_policy): ООО / ИП / allowlist.
 * GET /access/me is auth-only — ждём authReady, silent401 до/после.
 */
export function useConcurensAccess({ enabled = true } = {}) {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const checkingAuth = useSelector(selectCheckingAuth);
  const authReady = isAuthenticated && !checkingAuth;
  const canFetch = Boolean(enabled) && authReady;

  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(Boolean(canFetch));
  const [error, setError] = useState(null);
  const [reason, setReason] = useState(null);
  const [message, setMessage] = useState(null);
  const [policySummary, setPolicySummary] = useState(null);

  const reload = useCallback(async () => {
    if (!canFetch) {
      setAllowed(false);
      setLoading(false);
      setError(null);
      setReason(null);
      setMessage(null);
      setPolicySummary(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${CONCURENS_API_ADMIN_BASE}/access/me`, {
        silent401: true,
        cacheTtlMs: 60_000,
      });
      const json = res?.data || {};
      setAllowed(Boolean(json.allowed));
      setReason(json.reason || null);
      setMessage(json.message || null);
      setPolicySummary(json.policy_summary || null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        reportSessionUnauthorized({
          url: `${CONCURENS_API_ADMIN_BASE}/access/me`,
          status: 401,
          code: err?.response?.data?.code,
        });
      }
      setAllowed(false);
      setError(err);
      setReason(status === 401 ? 'AUTH_REQUIRED' : 'CONCURENS_ACCESS_CHECK_FAILED');
      setMessage(
        status === 401
          ? 'Требуется вход в систему'
          : (err?.response?.data?.message || err?.message || 'Не удалось проверить доступ')
      );
      setPolicySummary(null);
    } finally {
      setLoading(false);
    }
  }, [canFetch]);

  useEffect(() => {
    reload();
  }, [reload]);

  return {
    allowed,
    loading,
    error,
    reason,
    message,
    policySummary,
    reload,
  };
}
